import { type KeyboardEvent, type ReactNode } from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, IconButton, InputAdornment, TextField, type TextFieldProps } from '@mui/material';

import { useDictatedValue } from '../hooks/useDictatedValue';
import { type DictationController, useDictation } from '../hooks/useDictation';
import { useMicrophones } from '../hooks/useMicrophones';

import { DictationButton } from './DictationButton';

export interface DictationTextFieldOptions {
  /** Analytics label for this field, e.g. `jot_support`. */
  surface: string;
  keyterms?: string[];
  prompt?: string;
  disabled?: boolean;
}

export type DictationTextFieldProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'defaultValue' | 'helperText'
> & {
  value: string;
  onValueChange: (value: string) => void;
  dictation: DictationTextFieldOptions;
  helperText?: ReactNode;
};

function ErrorLine({ controller }: { controller: DictationController }) {
  return (
    <Box
      component="span"
      role="alert"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}
    >
      {controller.error}
      <IconButton
        aria-label="Dismiss dictation message"
        size="small"
        onClick={controller.dismissError}
        sx={{ p: 0.25 }}
      >
        <CloseRounded sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

/**
 * A MUI TextField with a dictation button in its corner. Spoken words appear in
 * the field as they are recognized; each pause commits a formatted sentence.
 * Typing is paused while listening so the two never fight over the value.
 * Listening shows as a red outline and the pulsing stop button; nothing is
 * added below the field except an error, so the layout holds still.
 *
 * Without a mounted `DictationRuntimeProvider` (or with the gate off) it is a
 * plain TextField.
 */
export function DictationTextField({
  value,
  onValueChange,
  dictation,
  disabled,
  helperText,
  slotProps,
  onKeyDown,
  multiline,
  ...rest
}: DictationTextFieldProps) {
  const microphones = useMicrophones();
  const controller = useDictation({
    ...dictation,
    deviceId: microphones.selected?.browserDeviceId,
    disabled: disabled || dictation.disabled,
  });
  const inputSlot = typeof slotProps?.input === 'function' ? undefined : slotProps?.input;
  const htmlInputSlot =
    typeof slotProps?.htmlInput === 'function'
      ? undefined
      : (slotProps?.htmlInput as Record<string, unknown> | undefined);
  const maxLength =
    typeof htmlInputSlot?.maxLength === 'number' ? htmlInputSlot.maxLength : undefined;
  useDictatedValue({ value, onValueChange, controller, maxLength });
  const listening = controller.status === 'listening';
  // Typing pauses from the moment dictation starts: the value at that moment is
  // the base spoken text is appended to, so later keystrokes would be lost.
  const paused = controller.status !== 'idle';

  const endAdornment = controller.available ? (
    <InputAdornment
      position="end"
      sx={{ alignSelf: multiline ? 'flex-end' : 'center', mb: multiline ? 0.5 : 0 }}
    >
      <DictationButton controller={controller} microphones={microphones} />
    </InputAdornment>
  ) : (
    inputSlot?.endAdornment
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && listening) {
      event.preventDefault();
      event.stopPropagation();
      controller.stop();
      return;
    }
    onKeyDown?.(event);
  };

  return (
    <TextField
      {...rest}
      multiline={multiline}
      value={value}
      onChange={event => onValueChange(event.target.value)}
      disabled={disabled}
      onKeyDown={handleKeyDown}
      color={listening ? 'error' : rest.color}
      focused={listening ? true : rest.focused}
      helperText={controller.error ? <ErrorLine controller={controller} /> : helperText}
      slotProps={{
        ...slotProps,
        input: {
          ...inputSlot,
          readOnly: paused || inputSlot?.readOnly,
          endAdornment,
        },
      }}
    />
  );
}
